import { useQueryClient } from '@tanstack/react-query'
import { useContext, useState } from 'react'

import NAVAssignmentDropdown from 'components/brokers/needs_attention/NAVAssignmentDropdown'
import {
  filterByLabelAndExactValueCallback,
  setQueryDataCallback,
} from 'components/brokers/needs_attention/utils'
import BrokerContext from 'components/context/BrokerContext'
import ReimbursementsContext from 'components/context/ReimbursementsContext'
import { useGetBrokers } from 'components/hooks/needs_attention/useGetBrokers'
import { useModifyReimbursments } from 'components/hooks/reimbursements/useModifyReimbursements'
import ComboboxMultiSelectFilter, {
  type ComboboxFilterOption,
} from 'components/reusable/table/ComboboxMultiSelectFilter'
import { type BrokersBroker } from 'utils/web_api.schemas'

type AssignTagsToNAVItemProps = {
  id: string
  assignedBrokers: BrokersBroker[]
}

const AssignBrokersToReimbursement = ({
  id,
  assignedBrokers,
}: AssignTagsToNAVItemProps) => {
  const queryClient = useQueryClient()
  const { getReimbursementsQueryKey } = useContext(ReimbursementsContext)
  const { currentBroker } = useContext(BrokerContext)
  const [query, setQuery] = useState('')

  const { data } = useGetBrokers()
  const brokers = data?.data as BrokersBroker[]

  const pinnedBrokerOptions: ComboboxFilterOption<string>[] = [
    ...(currentBroker
      ? [{ label: currentBroker?.broker_name, value: currentBroker?.id }]
      : []),
  ]

  const brokerOptions: ComboboxFilterOption<string>[] =
    brokers
      ?.map((broker) => ({
        label: broker.broker_name,
        value: broker.id,
      }))
      .filter((option) => option.value !== currentBroker?.id) || []

  const addAssigneesToReimbursementMutation = useModifyReimbursments({
    ids: [id],
  })

  const assignedBrokerIds = assignedBrokers.map((broker) => broker.id)

  const handleSelectTag = (brokerId: string) => {
    addAssigneesToReimbursementMutation.mutate(
      {
        ...(assignedBrokerIds.includes(brokerId)
          ? { broker_ids_to_remove: [brokerId] }
          : { broker_ids_to_add: [brokerId] }),
      },
      {
        onSuccess: (data) => {
          // Update cached data with new NAV items that include updated tags.
          // TODO: Investigate looking at query params and removing item if not part of the current page.
          queryClient.setQueryData(
            getReimbursementsQueryKey,
            setQueryDataCallback(data)
          )
        },
      }
    )
  }

  const filteredPinnedBrokerOptions = pinnedBrokerOptions.filter(
    filterByLabelAndExactValueCallback(query)
  )
  const filteredBrokerOptions = brokerOptions.filter(
    filterByLabelAndExactValueCallback(query)
  )

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <ComboboxMultiSelectFilter<string>
        selectedValues={assignedBrokerIds}
        options={[
          { groupLabel: 'Pinned', options: filteredPinnedBrokerOptions },
          { groupLabel: 'Brokers', options: filteredBrokerOptions },
        ]}
        onSelectValue={handleSelectTag}
        placeholder='Search brokers to assign...'
        onValueChange={setQuery}
      >
        <NAVAssignmentDropdown
          noSelectionText='Assignee'
          pluralIdentifier='assignees'
          selectedValues={assignedBrokerIds}
          options={[...pinnedBrokerOptions, ...brokerOptions]}
          isPending={addAssigneesToReimbursementMutation.isPending}
          type='assignee'
        />
      </ComboboxMultiSelectFilter>
    </div>
  )
}

export default AssignBrokersToReimbursement
